import { DG } from '@cotera/sdk/core';
import { Assert } from '@cotera/utilities';

/**
 * Gets all parent datasets in the lineage of a given dataset.
 * This includes direct dependencies and transitive dependencies.
 */
function getAllParentDatasets(
  graph: DG.BuildExplorer,
  datasetName: string,
  visited: Set<string> = new Set()
): string[] {
  if (visited.has(datasetName)) {
    return [];
  }
  visited.add(datasetName);

  const graphResult = graph.graph[datasetName];
  if (!graphResult || graphResult.isErr()) {
    return [];
  }

  const deps = Object.keys(graphResult.value.deps);
  const allParents: string[] = [...deps];

  // Recursively get parents of parents
  for (const dep of deps) {
    const parentParents = getAllParentDatasets(graph, dep, visited);
    allParents.push(...parentParents);
  }

  return allParents;
}

/**
 * Gets all column names from a dataset in the graph.
 */
function getColumnNamesFromDataset(
  graph: DG.BuildExplorer,
  datasetName: string
): string[] {
  const relNode = graph.getRel({ symbol: datasetName });
  if (!relNode) {
    return [];
  }

  return relNode.builtNode.match(
    (r) => {
      Assert.assert(r.item.t === 'rel');
      return Object.keys(r.item.cols);
    },
    (_) => []
  );
}

/**
 * Checks if a column name exists in the current dataset or any parent dataset in its lineage.
 * Returns the name of the conflicting dataset if found, or null if no conflict exists.
 *
 * @param graph - The graph build explorer
 * @param datasetName - The name of the dataset to check
 * @param columnName - The column name to check for conflicts
 * @param excludeCurrentColumn - If true, excludes the current column from the check (useful for renames)
 * @returns The name of the dataset where the conflict was found, or null if no conflict
 */
export function findColumnNameConflict(
  graph: DG.BuildExplorer,
  datasetName: string,
  columnName: string,
  excludeCurrentColumn: boolean = false
): string | null {
  // Check current dataset
  const currentColumns = getColumnNamesFromDataset(graph, datasetName);
  if (currentColumns.includes(columnName)) {
    if (!excludeCurrentColumn) {
      return datasetName;
    }
  }

  // Check all parent datasets
  const parentDatasets = getAllParentDatasets(graph, datasetName);
  for (const parentDataset of parentDatasets) {
    const parentColumns = getColumnNamesFromDataset(graph, parentDataset);
    if (parentColumns.includes(columnName)) {
      return parentDataset;
    }
  }

  return null;
}
